import React, { useState, useEffect } from 'react';
import { Globe, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';

const LANGS = [
  { label: 'English', code: 'en' },
  { label: 'Español', code: 'es' },
  { label: 'Français', code: 'fr' },
  { label: 'Deutsch', code: 'de' },
  { label: '中文', code: 'zh-CN' },
  { label: 'हिन्दी', code: 'hi' },
];

const COOKIE_NAME = 'googtrans';

const setGoogTransCookie = (code) => {
  const value = code === 'en' ? '' : `/en/${code}`;
  // Set on both path variants + strip subdomain for cross-subdomain persistence
  const host = window.location.hostname;
  const parts = host.split('.');
  const rootDomain = parts.length > 2 ? '.' + parts.slice(-2).join('.') : host;
  document.cookie = `${COOKIE_NAME}=${value};path=/`;
  document.cookie = `${COOKIE_NAME}=${value};path=/;domain=${host}`;
  document.cookie = `${COOKIE_NAME}=${value};path=/;domain=${rootDomain}`;
};

const readGoogTransCookie = () => {
  const m = document.cookie.match(/(?:^|; )googtrans=([^;]+)/);
  if (!m) return 'en';
  const raw = decodeURIComponent(m[1]);
  const parts = raw.split('/');
  return parts[2] || 'en';
};

const TopBar = ({ onOpenPortal }) => {
  const initialCode = typeof window !== 'undefined' ? readGoogTransCookie() : 'en';
  const initialLang = LANGS.find((l) => l.code === initialCode) || LANGS[0];
  const [lang, setLang] = useState(initialLang);
  const [open, setOpen] = useState(false);

  // Inject Google Translate widget once (global, hidden container)
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.__googleTranslateInjected) return;
    window.__googleTranslateInjected = true;

    // Global init callback expected by GT script
    window.googleTranslateElementInit = () => {
      try {
        // eslint-disable-next-line no-new, no-undef
        new google.translate.TranslateElement(
          {
            pageLanguage: 'en',
            includedLanguages: LANGS.map((l) => l.code).join(','),
            autoDisplay: false,
            layout: 0, // SIMPLE
          },
          'google_translate_element'
        );
      } catch (e) {}
    };

    // Ensure hidden container exists
    if (!document.getElementById('google_translate_element')) {
      const div = document.createElement('div');
      div.id = 'google_translate_element';
      div.style.display = 'none';
      document.body.appendChild(div);
    }

    // Inject GT script
    const s = document.createElement('script');
    s.src = 'https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
    s.async = true;
    document.head.appendChild(s);

    // Hide the GT top banner it sometimes adds
    const style = document.createElement('style');
    style.innerHTML = `
      .skiptranslate, .goog-te-banner-frame, #goog-gt-tt, .goog-tooltip { display: none !important; }
      body { top: 0 !important; }
      .goog-te-gadget { font-size: 0 !important; }
    `;
    document.head.appendChild(style);
  }, []);

  const pick = (l) => {
    setLang(l);
    setOpen(false);
    setGoogTransCookie(l.code);
    // Reload so the widget applies the new language across all rendered nodes
    window.location.reload();
  };

  return (
    <div className="w-full flex items-center justify-end gap-3 pt-6 pb-4 px-4 md:px-10">
      <div className="relative">
        <button
          data-testid="lang-switcher-btn"
          onClick={() => setOpen(!open)}
          className="flex items-center gap-2 rounded-full border border-emerald-700/30 bg-white px-4 py-2 text-sm text-emerald-900 hover:bg-emerald-50 shadow-sm transition"
        >
          <Globe size={16} className="text-emerald-600" />
          <span>{lang.label}</span>
          <ChevronDown size={14} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
        {open && (
          <div data-testid="lang-menu" className="absolute right-0 mt-2 w-40 rounded-xl border border-emerald-900/15 bg-white shadow-xl p-1.5 z-50">
            {LANGS.map((l) => (
              <button
                key={l.code}
                data-testid={`lang-opt-${l.code}`}
                onClick={() => pick(l)}
                className={`w-full text-left px-3 py-2 text-sm rounded-lg transition ${lang.code === l.code ? 'bg-emerald-100 text-emerald-950 font-semibold' : 'text-emerald-900 hover:bg-emerald-50'}`}
              >
                {l.label}
              </button>
            ))}
          </div>
        )}
      </div>

      <Button
        onClick={onOpenPortal}
        className="rounded-full bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-medium px-5 shadow-[0_0_24px_rgba(34,197,94,0.35)]"
      >
        <span className="h-2 w-2 rounded-full bg-emerald-950 mr-2 animate-pulse-dot" />
        Login
      </Button>
    </div>
  );
};

export default TopBar;
